import React, { useEffect, useState, useCallback } from 'react';
import { getReports, getEmployees } from '../api';
import { formatDuration, weekRange } from '../utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { useAuth } from '../AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const QUICK_RANGES = [
  { label: 'This Week', fn: () => { const { from, to } = weekRange(); return { from: from.split('T')[0], to: to.split('T')[0] }; } },
  { label: 'Last Week', fn: () => {
    const now = new Date();
    const day = now.getDay();
    const mon = new Date(now); mon.setDate(now.getDate() - day - 6); mon.setHours(0,0,0,0);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: mon.toISOString().split('T')[0], to: sun.toISOString().split('T')[0] };
  }},
  { label: 'This Month', fn: () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    return { from, to };
  }},
  { label: 'Last Month', fn: () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
    const to = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    return { from, to };
  }},
];

export default function Reports() {
  const { user, isAdmin } = useAuth();
  const { from: wFrom, to: wTo } = weekRange();
  const [from, setFrom] = useState(wFrom.split('T')[0]);
  const [to, setTo] = useState(wTo.split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('summary');
  const [employees, setEmployees] = useState([]);
  const [filterEmp, setFilterEmp] = useState('all'); // 'all' or employee id

  // Load employee list for admin filter dropdown
  useEffect(() => {
    if (isAdmin) getEmployees().then(setEmployees).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: from + 'T00:00:00', to: to + 'T23:59:59' };
      if (!isAdmin && user?.employee_id) params.employee_id = user.employee_id;
      else if (isAdmin && filterEmp !== 'all') params.employee_id = filterEmp;
      const res = await getReports(params);
      setData(res);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [from, to, isAdmin, user?.employee_id, filterEmp]);

  useEffect(() => { load(); }, [load]);

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    const empName = filterEmp !== 'all'
      ? employees.find(e => e.id === filterEmp)?.name || 'Employee'
      : 'All Employees';
    const title = `Work Report — ${empName}`;
    const subtitle = `Period: ${from} to ${to}`;

    doc.setFontSize(18);
    doc.setTextColor(79, 70, 229);
    doc.text(title, 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(subtitle, 14, 26);
    doc.setTextColor(30, 41, 59);

    // Summary stats
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', 14, 36);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    const totalWorkedSecs = data.summary.reduce((s, e) => s + (e.worked_seconds ?? e.total_seconds), 0);
    const totalActiveSecs = data.summary.reduce((s, e) => s + (e.active_seconds || 0), 0);
    const totalSess = data.summary.reduce((s, e) => s + e.sessions, 0);
    const totalDaysW = data.summary.reduce((s, e) => s + e.days_worked, 0);
    const wdp = data.working_days_period || 0;
    const single = filterEmp !== 'all' || !isAdmin;
    const expActiveH = (data.expected_active_seconds || 0) / 3600;
    const stats = [
      `Total Working Hours: ${(totalWorkedSecs / 3600).toFixed(1)}h`,
      `Active Hours: ${(totalActiveSecs / 3600).toFixed(1)}${single ? '/' + expActiveH.toFixed(0) + 'h' : 'h'}`,
      `Days Worked: ${totalDaysW}${single ? '/' + wdp : ''}`,
      `Sessions: ${totalSess}`,
      `Employees: ${data.summary.length}`,
    ];
    doc.text(stats.join('    '), 14, 43);

    // Employee summary table
    doc.setFont(undefined, 'bold');
    doc.setFontSize(11);
    doc.text('Employee Summary', 14, 52);
    autoTable(doc, {
      startY: 55,
      head: [['Employee', 'Department', 'Total Working Hrs', 'Active Hrs', 'Days Worked', 'Sessions', 'Avg Daily (h)']],
      body: data.summary.map(s => [
        s.name,
        s.department || '—',
        ((s.worked_seconds ?? s.total_seconds) / 3600).toFixed(1) + 'h',
        `${((s.active_seconds || 0) / 3600).toFixed(1)}/${((s.expected_active_seconds || 0) / 3600).toFixed(0)}h`,
        `${s.days_worked}/${s.working_days_period ?? '—'}`,
        s.sessions,
        (s.avg_daily_seconds / 3600).toFixed(1) + 'h',
      ]),
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [240, 244, 255] },
      margin: { left: 14, right: 14 },
    });

    // Time entries table
    const afterSummary = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Time Entries', 14, afterSummary);
    autoTable(doc, {
      startY: afterSummary + 3,
      head: [['Employee', 'Date', 'Clock In', 'Clock Out', 'Duration', 'Note', 'Type']],
      body: data.entries.map(e => [
        e.employee_name,
        e.clock_in.split('T')[0],
        new Date(e.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        e.clock_out ? new Date(e.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Active',
        e.duration_seconds ? formatDuration(e.duration_seconds) : '—',
        e.note || '—',
        e.manual ? 'Manual' : 'Auto',
      ]),
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Generated by HRTrack — Page ${i} of ${pageCount}`, 14, doc.internal.pageSize.height - 8);
    }

    const filename = `report_${empName.replace(/\s+/g,'_')}_${from}_${to}.pdf`;
    doc.save(filename);
  };

  // Derived stats
  const totalSeconds = data?.summary.reduce((s, e) => s + e.total_seconds, 0) || 0;
  const totalWorked = data?.summary.reduce((s, e) => s + (e.worked_seconds ?? e.total_seconds), 0) || 0;
  const totalActive = data?.summary.reduce((s, e) => s + (e.active_seconds || 0), 0) || 0;
  const totalSessions = data?.summary.reduce((s, e) => s + e.sessions, 0) || 0;
  const totalDays = data?.summary.reduce((s, e) => s + e.days_worked, 0) || 0;
  const workingDaysPeriod = data?.working_days_period || 0;
  // The expected/target denominators only make sense for a single person, so they
  // are shown only when the report is filtered to one employee.
  const singleEmployee = !isAdmin || filterEmp !== 'all';
  const expectedActiveH = (data?.expected_active_seconds || 0) / 3600;
  const avgDaily = totalDays ? (totalWorked / totalDays / 3600).toFixed(1) : '0.0';
  const manualCount = data?.entries.filter(e => e.manual).length || 0;
  const fmt1 = (secs) => (secs / 3600).toFixed(1);

  const chartData = data?.summary.map(s => ({
    name: s.name.split(' ')[0],
    hours: +(s.total_seconds / 3600).toFixed(1),
    avg_daily: +(s.avg_daily_seconds / 3600).toFixed(1),
    days: s.days_worked,
    sessions: s.sessions,
  })) || [];

  const dailyMap = {};
  (data?.entries || []).forEach(e => {
    const date = e.clock_in.split('T')[0];
    if (!dailyMap[date]) dailyMap[date] = 0;
    dailyMap[date] += (e.duration_seconds || 0) / 3600;
  });
  const dailyData = Object.keys(dailyMap).sort().map(date => ({
    date: new Date(date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    hours: +dailyMap[date].toFixed(1),
  }));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <button className="btn btn-success" onClick={exportPDF} disabled={!data}>↓ Export PDF</button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        {/* Quick ranges */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {QUICK_RANGES.map(r => (
            <button key={r.label} className="btn btn-outline btn-sm"
              onClick={() => { const { from: f, to: t } = r.fn(); setFrom(f); setTo(t); }}>
              {r.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>

          {/* Employee filter — admin only */}
          {isAdmin && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Employee</label>
              <select value={filterEmp} onChange={e => setFilterEmp(e.target.value)} style={{ minWidth: 160 }}>
                <option value="all">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
          )}

          <button className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Apply'}
          </button>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {['summary', 'entries'].map(v => (
              <button key={v} className={`btn ${view === v ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setView(v)} style={{ textTransform: 'capitalize' }}>{v}</button>
            ))}
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Stats row */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <div className="stat-label">Total Working Hours</div>
              <div className="stat-value">{fmt1(totalWorked)}</div>
              <div className="stat-sub">clocked-in, incl. idle</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Active Hours</div>
              <div className="stat-value" style={{ fontSize: singleEmployee ? '1.55rem' : undefined }}>{fmt1(totalActive)}{singleEmployee && <span style={{ color: 'var(--muted)', fontWeight: 600 }}> / {expectedActiveH.toFixed(0)}h</span>}</div>
              <div className="stat-sub">{singleEmployee ? 'active vs expected' : 'active (excl. idle)'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Days Worked</div>
              <div className="stat-value">{totalDays}{singleEmployee && <span style={{ color: 'var(--muted)', fontWeight: 600 }}> / {workingDaysPeriod}</span>}</div>
              <div className="stat-sub">{singleEmployee ? 'of working days' : 'unique work days'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg Daily</div>
              <div className="stat-value">{avgDaily}h</div>
              <div className="stat-sub">per work day</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Sessions</div>
              <div className="stat-value">{totalSessions}</div>
              <div className="stat-sub">clock-in events</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Manual Entries</div>
              <div className="stat-value">{manualCount}</div>
              <div className="stat-sub">of {data.entries.length} total</div>
            </div>
            {isAdmin && (
              <div className="stat-card">
                <div className="stat-label">Employees</div>
                <div className="stat-value">{data.summary.length}</div>
                <div className="stat-sub">with activity</div>
              </div>
            )}
          </div>

          {view === 'summary' && (
            <>
              <div className="grid-2" style={{ marginBottom: 20 }}>
                <div className="card">
                  <div className="card-title">Hours by Employee</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={v => [`${v}h`, 'Hours']} />
                      <Bar dataKey="hours" fill="var(--primary)" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <div className="card-title">Daily Hours Trend</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={v => [`${v}h`, 'Hours']} />
                      <Line type="monotone" dataKey="hours" stroke="var(--primary)" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Avg daily hours chart */}
              {isAdmin && chartData.length > 1 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <div className="card-title">Avg Daily Hours by Employee</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={v => [`${v}h`, 'Avg Daily']} />
                      <Bar dataKey="avg_daily" fill="var(--accent)" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div className="card-title" style={{ marginBottom: 0 }}>
                    {filterEmp !== 'all' ? `Report — ${employees.find(e => e.id === filterEmp)?.name}` : 'Employee Summary'}
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={exportPDF}>↓ PDF</button>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Total Working Hrs</th>
                        <th>Active Hrs</th>
                        <th>Days Worked</th>
                        <th>Sessions</th>
                        <th>Avg Daily</th>
                        <th>Longest Day</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.summary.map(s => {
                        // Compute longest single-day hours for this employee
                        const empEntries = data.entries.filter(e => e.employee_id === s.employee_id);
                        const dayTotals = {};
                        empEntries.forEach(e => {
                          const d = e.clock_in.split('T')[0];
                          dayTotals[d] = (dayTotals[d] || 0) + (e.duration_seconds || 0);
                        });
                        const longestDay = Object.values(dayTotals).length
                          ? Math.max(...Object.values(dayTotals))
                          : 0;
                        return (
                          <tr key={s.employee_id}>
                            <td style={{ fontWeight: 600 }}>{s.name}</td>
                            <td className="text-muted">{s.department || '—'}</td>
                            <td>{((s.worked_seconds ?? s.total_seconds) / 3600).toFixed(1)}h</td>
                            <td>{((s.active_seconds || 0) / 3600).toFixed(1)}<span className="text-muted"> / {((s.expected_active_seconds || 0) / 3600).toFixed(0)}h</span></td>
                            <td>{s.days_worked}<span className="text-muted"> / {s.working_days_period ?? '—'}</span></td>
                            <td>{s.sessions}</td>
                            <td>{(s.avg_daily_seconds / 3600).toFixed(1)}h</td>
                            <td>{longestDay ? (longestDay / 3600).toFixed(1) + 'h' : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {view === 'entries' && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div className="card-title" style={{ marginBottom: 0 }}>Time Entries ({data.entries.length})</div>
                <button className="btn btn-outline btn-sm" onClick={exportPDF}>↓ PDF</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Clock In</th>
                      <th>Clock Out</th>
                      <th>Duration</th>
                      <th>Note</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map(e => (
                      <tr key={e.id}>
                        <td style={{ fontWeight: 500 }}>{e.employee_name}</td>
                        <td>{new Date(e.clock_in + (e.clock_in.endsWith('Z') ? '' : 'Z')).toLocaleDateString()}</td>
                        <td>{new Date(e.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                        <td>{e.clock_out ? new Date(e.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : <span className="badge badge-green">Active</span>}</td>
                        <td style={{ fontWeight: 500 }}>{e.duration_seconds ? formatDuration(e.duration_seconds) : '—'}</td>
                        <td className="text-muted" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.note || '—'}</td>
                        <td><span className={`badge ${e.manual ? 'badge-orange' : 'badge-blue'}`}>{e.manual ? 'Manual' : 'Auto'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {!data && !loading && (
        <div className="card empty">No data for selected period.</div>
      )}
    </div>
  );
}
